import type {
  HeldFor,
  HoldOutcome,
  OperationEntry,
  PlayerHistoryItem,
  ReleaseReason,
} from "@pax/api-contract/accounting/v1alpha1";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useMyPermissions } from "@/features/auth/permissions";
import { useSession } from "@/features/auth/queries";
import { readableCode } from "@/features/credits/journals";
import { spendableRaw } from "@/features/credits/queries";
import { button, Disclosure, Section } from "@/features/evals/components";
import { Problem } from "@/features/staff/components";
import { BalanceGraph } from "./BalanceGraph";
import type { HistoryWindow } from "./balances";
import {
  Credits,
  EntryTable,
  ExpandAllToggle,
  GameName,
  NotCharged,
  Note,
  OutcomeTag,
  Pager,
  type PageSize,
  PageSizePicker,
  PersonName,
  pickedRow,
  presetName,
  Table,
  When,
} from "./components";
import { effectKindLabel, sourceSystemLabel } from "./effects";
import {
  agentLabel,
  bucketChange,
  bucketLabels,
  exactMoment,
  localMoment,
  outcomeLabels,
  supportReasonLabels,
} from "./format";
import { GameIdFilter, InstantPicker } from "./pickers";
import {
  fetchPlayerHistoryNewerStart,
  historyStart,
  historyStartValue,
  playerBalanceQuery,
  playerHistoryQuery,
  playerMembershipsQuery,
  playerOpenHoldsQuery,
  playerPaidGameQuery,
} from "./queries";
import { foldRuns } from "./runs";
import { SupportAdjustmentForm } from "./SupportAdjustmentForm";
import type { GraphSettings } from "./TimeGraph";

/**
 * One player's balances, open holds, games, and history newest first, and, for credit managers, the
 * support form.
 */
export function PlayerView({
  gameId,
  onGameIdChange,
  graph,
  onGraphChange,
  ...history
}: HistoryProps & {
  /** The one game the games list finds by id. */
  gameId: string | undefined;
  onGameIdChange: (gameId: string | undefined) => void;
  graph: GraphSettings;
  onGraphChange: (patch: Partial<GraphSettings>) => void;
}) {
  const { userId } = history;
  const { data: session } = useSession();
  const { permissions } = useMyPermissions(session?.user);
  const [adjusting, setAdjusting] = useState(false);
  /** A picked span, and the start the list jumped to for it; none when it only stands out. */
  const [picked, setPicked] = useState<{
    window: HistoryWindow;
    start?: string;
    scroll?: boolean;
  } | null>(null);
  const pick = (window: HistoryWindow, scroll: boolean) => {
    const start = new Date(window.to).toISOString();
    setPicked({ window, start, scroll });
    history.onStartChange(start);
  };
  const jumped = picked?.start !== undefined;
  return (
    <>
      <Balances
        userId={userId}
        pageSize={history.pageSize}
        graph={graph}
        onGraphChange={onGraphChange}
        onSelect={pick}
        onHighlight={window => setPicked({ window })}
      />
      <OpenHolds userId={userId} />
      {permissions.includes("credits.manage") && (
        <Section label="Grant or remove">
          {adjusting ? (
            <SupportAdjustmentForm userId={userId} close={() => setAdjusting(false)} />
          ) : (
            <button type="button" className={button} onClick={() => setAdjusting(true)}>
              Grant or remove credits
            </button>
          )}
        </Section>
      )}
      <TheirGames userId={userId} gameId={gameId} onGameIdChange={onGameIdChange} />
      <History
        {...history}
        picked={picked && (!jumped || picked.start === history.start) ? picked.window : null}
        scrollToPicked={picked?.scroll ?? false}
      />
    </>
  );
}

const totalledBuckets = (Object.keys(bucketLabels) as (keyof typeof bucketLabels)[]).filter(
  bucket => bucket !== "moderation",
);

function Balances({
  userId,
  pageSize,
  graph,
  onGraphChange,
  onSelect,
  onHighlight,
}: {
  userId: string;
  pageSize: PageSize;
  graph: GraphSettings;
  onGraphChange: (patch: Partial<GraphSettings>) => void;
  onSelect: (window: HistoryWindow, scroll: boolean) => void;
  onHighlight: (window: HistoryWindow) => void;
}) {
  const balance = useQuery(playerBalanceQuery(userId));
  if (balance.error) return <Problem error={balance.error} />;
  if (!balance.data) return <Note>Loading balances…</Note>;
  const { buckets, heldRaw } = balance.data;
  return (
    <Section label="Balance">
      <Disclosure
        className="border-t border-foreground/[0.07] py-1"
        summary={
          <span className="flex items-baseline justify-between gap-4">
            <span>Current balance</span>
            <Credits raw={spendableRaw(buckets).toString()} />
          </span>
        }
      >
        {() => (
          <div className="pb-2">
            <Table
              columns={[{ label: "Balance" }, { label: "Credits", numeric: true }]}
              rows={totalledBuckets.map(bucket => ({
                key: bucket,
                cells: [bucketLabels[bucket], <Credits key="amount" raw={buckets[bucket]} />],
              }))}
            />
            <div className="mt-3 border-t border-foreground/15 pt-3">
              <Table
                columns={[{ label: "Not in total" }, { label: "Credits", numeric: true }]}
                rows={[
                  {
                    key: "moderation",
                    cells: [
                      bucketLabels.moderation,
                      <Credits key="amount" raw={buckets.moderation} />,
                    ],
                  },
                  {
                    key: "held",
                    cells: ["Held", <Credits key="amount" raw={heldRaw} />],
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Disclosure>
      <Disclosure
        className="border-t border-foreground/[0.07] py-1"
        summary="Balance over time"
        open={graph.graph === true}
        onOpenChange={open => onGraphChange({ graph: open || undefined })}
      >
        {() => (
          <BalanceGraph
            userId={userId}
            pageSize={pageSize}
            settings={graph}
            onSettingsChange={onGraphChange}
            onSelect={onSelect}
            onHighlight={onHighlight}
          />
        )}
      </Disclosure>
    </Section>
  );
}

/** The player's holds that are placed and not yet settled or released, and what they reserve. */
function OpenHolds({ userId }: { userId: string }) {
  const holds = useQuery(playerOpenHoldsQuery(userId));
  if (holds.error) return <Problem error={holds.error} />;
  if (!holds.data) return <Note>Loading open holds…</Note>;
  const { openHolds, reservedTotalRaw } = holds.data;
  return (
    <Section label="Open holds">
      {openHolds.length === 0 ? (
        <Note>No open holds</Note>
      ) : (
        <Disclosure
          className="border-t border-foreground/[0.07] py-1"
          summary={
            <span className="flex items-baseline justify-between gap-4">
              <span>
                {openHolds.length} {openHolds.length === 1 ? "hold" : "holds"}
                <span className="text-muted-foreground"> · held</span>
              </span>
              <Credits raw={reservedTotalRaw} />
            </span>
          }
        >
          {() => (
            <div className="pb-2">
              <Table
                columns={[
                  { label: "Started" },
                  { label: "Expires" },
                  { label: "Hold" },
                  { label: "Held", numeric: true },
                ]}
                rows={openHolds.map(hold => ({
                  key: hold.holdId,
                  cells: [
                    <When key="started" iso={hold.startedAt} />,
                    exactMoment(hold.expiresAt),
                    <span key="hold" title={hold.holdRequestId} className="block max-w-80 truncate">
                      {heldForName(hold)}
                    </span>,
                    <Credits key="held" raw={hold.estimateRaw} />,
                  ],
                }))}
              />
            </div>
          )}
        </Disclosure>
      )}
    </Section>
  );
}

/** The games the player owns or has joined, newest activity first, or the one a game id finds. */
function TheirGames({
  userId,
  gameId,
  onGameIdChange,
}: {
  userId: string;
  gameId: string | undefined;
  onGameIdChange: (gameId: string | undefined) => void;
}) {
  return (
    <Section label="Games">
      <Disclosure
        className="border-t border-foreground/[0.07] py-1"
        initiallyOpen={!!gameId}
        summary="Games they own or joined"
      >
        {() => <GameList userId={userId} gameId={gameId} onGameIdChange={onGameIdChange} />}
      </Disclosure>
    </Section>
  );
}

function GameList({
  userId,
  gameId,
  onGameIdChange,
}: {
  userId: string;
  gameId: string | undefined;
  onGameIdChange: (gameId: string | undefined) => void;
}) {
  const memberships = useInfiniteQuery({
    ...playerMembershipsQuery(userId),
    enabled: !gameId,
  });
  const paid = useQuery({
    ...playerPaidGameQuery(userId, gameId ?? ""),
    enabled: !!gameId,
  });
  const found = gameId
    ? paid.data
      ? [paid.data]
      : []
    : (memberships.data?.pages.flatMap(page => page.memberships) ?? []);
  const error = gameId ? paid.error : memberships.error;
  const ready = gameId ? paid.isSuccess : memberships.isSuccess;
  return (
    <div className="space-y-2 pb-2">
      <div className="-ml-3">
        <GameIdFilter gameId={gameId} onChange={onGameIdChange} placeholder="Find a game by id" />
      </div>
      <Problem error={error} />
      {ready && found.length === 0 && <Note>{gameId ? "Not one of their games" : "No games"}</Note>}
      <ul className="text-sm">
        {found.map(game => (
          <li key={game.gameId} className="border-t border-foreground/[0.07] py-1.5">
            <GameName gameId={game.gameId} title={game.title} />
          </li>
        ))}
      </ul>
      {!gameId && memberships.hasNextPage && (
        <button
          type="button"
          className={button}
          disabled={memberships.isFetchingNextPage}
          onClick={() => void memberships.fetchNextPage()}
        >
          {memberships.isFetchingNextPage ? "Loading…" : "Show more games"}
        </button>
      )}
    </div>
  );
}

interface HistoryProps {
  userId: string;
  /** Where the history starts, as `historyStart` reads it. Missing starts at the newest. */
  start: string | undefined;
  onStartChange: (start: string | undefined) => void;
  /** Opens a game's holds kept to the ones this player paid for. */
  onShowGameHolds: (gameId: string) => void;
  pageSize: PageSize;
  onPageSizeChange: (pageSize: PageSize) => void;
  /** Every item on its own row, with no runs of alike holds folded together. */
  expandAll: boolean;
  onExpandAllChange: (expandAll: boolean) => void;
}

const itemKey = (item: PlayerHistoryItem) =>
  item.kind === "operation" ? `operation-${item.operationId}` : `hold-${item.holdId}`;

const itemTime = (item: PlayerHistoryItem) =>
  item.kind === "operation" ? item.recordedAt : item.releasedAt;

const itemHeldFor = (item: PlayerHistoryItem) => item.heldFor;

function itemOutcome(item: PlayerHistoryItem): HoldOutcome | ReleaseReason | undefined {
  if (item.kind === "releasedHold") return item.releaseReason;
  return item.heldFor ? "settled" : undefined;
}

/** A hold's game, kind, and how it ended; anything else has no run to join. */
function runKey(item: PlayerHistoryItem) {
  if (item.kind === "operation" && item.effectKind !== "hold_settlement") return undefined;
  const held = item.heldFor;
  return held ? `${held.gameId}|${held.kind}|${itemOutcome(item)}` : undefined;
}

/** Every balance a run's holds drew from, and what they moved them by in total. */
function runChange(items: readonly PlayerHistoryItem[]) {
  const totals = new Map<string, bigint>();
  for (const item of items) {
    if (item.kind !== "operation") continue;
    for (const [bucket, amount] of Object.entries(item.playerChanges)) {
      totals.set(bucket, (totals.get(bucket) ?? 0n) + BigInt(amount));
    }
  }
  return bucketChange(
    Object.fromEntries([...totals].map(([bucket, amount]) => [bucket, amount.toString()])),
  );
}

/** What released holds reserved in total, or null when none of them says. */
function reservedTotal(items: readonly PlayerHistoryItem[]) {
  const amounts = items.flatMap(item => (item.heldFor?.estimateRaw ? [item.heldFor] : []));
  if (amounts.length === 0) return null;
  return amounts.reduce((total, held) => total + BigInt(held.estimateRaw ?? "0"), 0n).toString();
}

function History({
  userId,
  start,
  onStartChange,
  onShowGameHolds,
  pageSize,
  onPageSizeChange,
  expandAll,
  onExpandAllChange,
  picked,
  scrollToPicked,
}: HistoryProps & {
  /** The span a click on the balance graph picked, whose rows stand out. */
  picked: HistoryWindow | null;
  /** The page scrolls to the list when it jumps to the picked span. */
  scrollToPicked: boolean;
}) {
  const listTop = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (picked && scrollToPicked) {
      listTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [picked, scrollToPicked]);
  const inPicked = (item: PlayerHistoryItem) => {
    const at = Date.parse(itemTime(item));
    return !!picked && at >= picked.from && at < picked.to;
  };
  const { at, before } = historyStart(start) ?? {};
  const [leaving, setLeaving] = useState(false);
  const history = useQuery(playerHistoryQuery(userId, { at, before, limit: pageSize }));
  const items = history.data?.items ?? [];
  const nextBefore = history.data?.nextBefore;
  const toNewer = async () => {
    setLeaving(true);
    try {
      onStartChange(await fetchPlayerHistoryNewerStart(userId, { at, before, limit: pageSize }));
    } finally {
      setLeaving(false);
    }
  };
  const pager = {
    label: pageSpan(items),
    loading: history.isPlaceholderData || leaving,
    toNewest: start ? () => onStartChange(undefined) : undefined,
    toNewer: start ? () => void toNewer() : undefined,
    toOlder: nextBefore
      ? () => onStartChange(historyStartValue({ at, before: nextBefore }))
      : undefined,
  };
  return (
    <Section label={at ? `History before ${exactMoment(at)}` : "History"}>
      <div className="-ml-3 mb-1 flex flex-wrap items-center gap-1">
        <InstantPicker at={at} onChange={onStartChange} />
        <span className="ml-3">
          <PageSizePicker pageSize={pageSize} onChange={onPageSizeChange} />
        </span>
        <span className="ml-2">
          <ExpandAllToggle expandAll={expandAll} onChange={onExpandAllChange} />
        </span>
      </div>
      <div ref={listTop} className="scroll-mt-4" />
      <Pager {...pager} />
      <Problem error={history.error} />
      {history.isSuccess && items.length === 0 && <Note>No history</Note>}
      {foldRuns(items, runKey, expandAll).map(row =>
        row.kind === "run" ? (
          <RunCard
            key={itemKey(row.items[0])}
            items={row.items}
            inPicked={inPicked}
            userId={userId}
            onShowGameHolds={onShowGameHolds}
          />
        ) : (
          <ItemCard
            key={itemKey(row.item)}
            item={row.item}
            picked={inPicked(row.item)}
            userId={userId}
            onShowGameHolds={onShowGameHolds}
          />
        ),
      )}
      <Pager {...pager} scrollTo={listTop} />
    </Section>
  );
}

/** When a page's items happened, oldest to newest, in place of a page number. */
function pageSpan(items: readonly PlayerHistoryItem[]) {
  const newest = items[0];
  const oldest = items.at(-1);
  if (!newest || !oldest) return "Nothing here";
  return `${localMoment(itemTime(oldest))} – ${localMoment(itemTime(newest))}`;
}

/** The hold kind, then the preset and game by title for a hold placed in a game. */
function heldForName(held: Pick<HeldFor, "kind" | "presetTitle" | "gameTitle" | "gameId">) {
  if (!held.gameId) return readableCode(held.kind);
  return [readableCode(held.kind), presetName(held.presetTitle), held.gameTitle ?? "Deleted game"]
    .filter(Boolean)
    .join(" · ");
}

/** What an item moved, signed, or for a released hold what it had reserved. */
function ItemAmount({ item }: { item: PlayerHistoryItem }) {
  if (item.kind === "releasedHold") return <NotCharged reservedRaw={item.heldFor.estimateRaw} />;
  const change = bucketChange(item.playerChanges);
  return change.buckets ? <Credits raw={change.amountRaw} signed toned /> : null;
}

/**
 * Back-to-back holds in one game that ended the same way, newest first: how many, how they ended,
 * the kind, what they moved in total, and when. Open it for each hold's time and amount, and
 * whatever else differs between them.
 */
function RunCard({
  items,
  inPicked,
  userId,
  onShowGameHolds,
}: {
  items: readonly [PlayerHistoryItem, ...PlayerHistoryItem[]];
  inPicked: (item: PlayerHistoryItem) => boolean;
  userId: string;
  onShowGameHolds: (gameId: string) => void;
}) {
  const newest = items[0];
  const oldest = items.at(-1) ?? newest;
  const held = newest.heldFor as HeldFor;
  const outcome = itemOutcome(newest) ?? "settled";
  const kinds = new Set(items.map(item => itemHeldFor(item)?.kind));
  const kind = kinds.size === 1 ? readableCode(held.kind) : `${kinds.size} kinds`;
  const agents = new Set(items.map(item => agentLabel(itemHeldFor(item)?.agent)));
  const [agent] = agents;
  const change = runChange(items);
  const bucketSets = new Set(
    items.map(item => (item.kind === "operation" ? bucketChange(item.playerChanges).buckets : "")),
  );
  return (
    <Disclosure
      stacked
      className={`border-t border-foreground/[0.07] py-1 ${items.some(inPicked) ? pickedRow : ""}`}
      summary={
        <span className="flex items-baseline justify-between gap-4">
          <span className="min-w-0">
            <span className="block truncate">
              {items.length} holds
              {change.buckets && <span className="text-muted-foreground"> · {change.buckets}</span>}
              <OutcomeTag outcome={outcome} />
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {[agents.size === 1 ? agent : `${agents.size} agents`, kind]
                .concat(heldForName(held).split(" · ").slice(1))
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end">
            {newest.kind === "releasedHold" ? (
              <NotCharged reservedRaw={reservedTotal(items)} />
            ) : (
              <Credits raw={change.amountRaw} signed toned />
            )}
            <span className="text-xs text-muted-foreground">
              <When iso={itemTime(oldest)} /> – <When iso={itemTime(newest)} />
            </span>
          </span>
        </span>
      }
    >
      {() => (
        <div className="pb-2">
          {items.map(item => (
            <Disclosure
              key={itemKey(item)}
              className={`border-t border-foreground/[0.07] ${inPicked(item) ? pickedRow : ""}`}
              summary={
                <span className="flex items-baseline justify-between gap-4 text-xs">
                  <span className="min-w-0 truncate">
                    <When iso={itemTime(item)} />
                    {kinds.size > 1 && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {readableCode(itemHeldFor(item)?.kind ?? "")}
                      </span>
                    )}
                    {bucketSets.size > 1 && item.kind === "operation" && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {bucketChange(item.playerChanges).buckets}
                      </span>
                    )}
                  </span>
                  <ItemAmount item={item} />
                </span>
              }
            >
              {() => <ItemDetails item={item} userId={userId} onShowGameHolds={onShowGameHolds} />}
            </Disclosure>
          ))}
        </div>
      )}
    </Disclosure>
  );
}

/** What happened, to which balances, by how much, and when; open it for the details. */
function ItemCard({
  item,
  picked,
  userId,
  onShowGameHolds,
}: {
  item: PlayerHistoryItem;
  picked: boolean;
  userId: string;
  onShowGameHolds: (gameId: string) => void;
}) {
  const outcome = itemOutcome(item);
  const held = itemHeldFor(item);
  let title = agentLabel(held?.agent) ?? "Hold";
  let buckets = "";
  let subtitle: ReactNode = held && heldForName(held);
  if (item.kind === "operation") {
    title = agentLabel(held?.agent) ?? effectKindLabel(item.effectKind);
    buckets = bucketChange(item.playerChanges).buckets;
    const support = item.supportAdjustment;
    const presetEntry = item.entries.find(entry => entry.presetId);
    if (support) {
      subtitle = `by ${support.granter.displayName}: ${supportReasonLabels[support.reason]} · ${support.note}`;
    } else if (!held && presetEntry) subtitle = presetName(presetEntry.presetTitle);
  }
  return (
    <Disclosure
      className={`border-t border-foreground/[0.07] py-1 ${picked ? pickedRow : ""}`}
      summary={
        <span className="flex items-baseline justify-between gap-4">
          <span className="min-w-0">
            <span className="block truncate">
              {title}
              {buckets && <span className="text-muted-foreground"> · {buckets}</span>}
              {outcome && <OutcomeTag outcome={outcome} />}
            </span>
            {subtitle && (
              <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
            )}
          </span>
          <span className="flex shrink-0 flex-col items-end">
            <ItemAmount item={item} />
            <span className="text-xs text-muted-foreground">
              <When iso={itemTime(item)} />
            </span>
          </span>
        </span>
      }
    >
      {() => <ItemDetails item={item} userId={userId} onShowGameHolds={onShowGameHolds} />}
    </Disclosure>
  );
}

/**
 * Where an item came from and the hold it reserved, then its entries and postings. The entries that
 * touch `userId` show first and the rest fold away.
 */
function ItemDetails({
  item,
  userId,
  onShowGameHolds,
}: {
  item: PlayerHistoryItem;
  userId: string;
  onShowGameHolds: (gameId: string) => void;
}) {
  const held = itemHeldFor(item);
  const outcome = itemOutcome(item);
  const details: [string, ReactNode][] =
    item.kind === "operation"
      ? [
          ["Kind", effectKindLabel(item.effectKind)],
          ["From", sourceSystemLabel(item.sourceSystem)],
          ...(item.supportAdjustment
            ? ([
                ["By", <PersonName key="by" person={item.supportAdjustment.granter} />],
                ["Reason", supportReasonLabels[item.supportAdjustment.reason]],
                ["Note", item.supportAdjustment.note],
              ] as [string, ReactNode][])
            : []),
          [held ? "Hold request" : "Request", <code key="request">{item.sourceRequestId}</code>],
          ["Operation", item.operationId],
        ]
      : [
          ["Released", <When key="released" iso={item.releasedAt} />],
          ["Hold", <code key="hold">{item.holdId}</code>],
        ];
  if (outcome) {
    details.splice(1, 0, ["Outcome", outcomeLabels[outcome]]);
  }
  if (held) {
    const { gameId } = held;
    const agent = agentLabel(held.agent);
    details.push(["Hold kind", <code key="kind">{held.kind}</code>]);
    if (agent) details.push(["Agent", agent]);
    if (gameId) {
      details.push(
        ["Preset", presetName(held.presetTitle)],
        [
          "Game",
          <span key="game" className="inline-flex flex-wrap items-baseline gap-x-2">
            <GameName gameId={gameId} title={held.gameTitle} />
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => onShowGameHolds(gameId)}
            >
              Only this game
            </button>
          </span>,
        ],
      );
    } else {
      details.push(["Game", "None, a direct call"]);
    }
    if (held.estimateRaw)
      details.push(["Reserved", <Credits key="reserved" raw={held.estimateRaw} />]);
  }
  const entries = item.kind === "operation" ? item.entries : [];
  const touches = (entry: OperationEntry) =>
    entry.postings.some(posting => posting.accountUserId === userId);
  const mine = entries.filter(touches);
  const others = entries.filter(entry => !touches(entry));
  return (
    <div className="space-y-3 pb-2">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-0.5 text-xs">
        {details.map(([term, detail]) => (
          <div key={term} className="contents">
            <dt className="text-muted-foreground">{term}</dt>
            <dd className="min-w-0 [overflow-wrap:anywhere]">{detail}</dd>
          </div>
        ))}
      </dl>
      {mine.map(entry => (
        <EntryTable key={entry.entryId} entry={entry} />
      ))}
      {others.length > 0 && (
        <Disclosure
          summary={
            <span className="text-muted-foreground">
              {others.length} other {others.length === 1 ? "entry" : "entries"}
            </span>
          }
        >
          {() => (
            <div className="space-y-3">
              {others.map(entry => (
                <EntryTable key={entry.entryId} entry={entry} />
              ))}
            </div>
          )}
        </Disclosure>
      )}
    </div>
  );
}
